import * as React from 'react';
import cn from 'clsx';
import { ArticleHeading1, ArticleHeading2, Image, UIText3 } from '@cian/ui-kit';
import { ConditionalWrapper } from '@cian/conditional-component';

import { IWithDeviceTypeProps, withDeviceType } from '../../../../utils/withDeviceType';
import { IAdditionalService } from '../../../../types/promoInfo';
import { ListWithIcons } from '../../../ListWithIcons';
import { X2_IMAGE_SIZE_MEDIA_QUERY } from '../../../../constants/mediaQueries';
import { AdditionalServiceAboutButton } from '../AdditionalServiceAboutButton';
import { AdditionalServiceBadge } from '../AdditionalServiceBadge';
import { AdditionalServicePrice } from '../AdditionalServicePrice';

import * as styles from './styles.css';

interface IAdditionalServiceCardExtendedProps extends IWithDeviceTypeProps, Omit<IAdditionalService, 'about'> {
  onAboutClick: () => void;
  extraActions?: React.ReactNode;
}

const AdditionalServiceCardExtended: React.FC<React.PropsWithChildren<IAdditionalServiceCardExtendedProps>> = ({
  title,
  badge,
  icon,
  image,
  price,
  details,
  onAboutClick,
  isPhone,
  deviceType,
  advertisingNotice,
  extraActions,
}) => {
  return (
    <section className={cn(styles['container'], styles[deviceType])}>
      {icon && (
        <span className={styles['icon']}>
          <img src={icon} width="60" height="60" alt="" />
        </span>
      )}
      <div className={styles['text-content']}>
        <ConditionalWrapper if={isPhone} ThenComponent={ArticleHeading2} ElseComponent={ArticleHeading1} as="h3">
          {title}
        </ConditionalWrapper>
        <div className={styles['details']}>
          <ListWithIcons.List>
            {details.map((detail, i) => (
              <ListWithIcons.Item key={i}>{detail}</ListWithIcons.Item>
            ))}
          </ListWithIcons.List>
        </div>
        {extraActions && <div className={styles['extra-actions']}>{extraActions}</div>}
        {badge && <AdditionalServiceBadge className={styles['badge']} badge={badge} />}
        {price && <AdditionalServicePrice className={styles['price']} price={price} />}
        <AdditionalServiceAboutButton
          className={cn(styles['button'], !badge && styles['with-top-margin'])}
          onClick={onAboutClick}
        />
        {advertisingNotice && (
          <div className={styles['advert']}>
            <UIText3 color="current_color">{advertisingNotice}</UIText3>
          </div>
        )}
      </div>
      {image && (
        <div className={cn(styles['image'], styles[deviceType])}>
          <Image
            width="100%"
            display="block"
            objectFit="contain"
            loading="lazy"
            src={image.x1}
            sources={[{ srcset: image.x2, media: X2_IMAGE_SIZE_MEDIA_QUERY }, { srcset: image.x1 }]}
          />
        </div>
      )}
    </section>
  );
};

const AdditionalServiceCardExtendedWithDeviceType = withDeviceType(AdditionalServiceCardExtended);
export { AdditionalServiceCardExtendedWithDeviceType as AdditionalServiceCardExtended };
